import { Box } from '@mui/system';
import { type ComponentProps, type ElementType, useEffect, useRef } from 'react';
import { padWithZeros } from '@/utils/padWithZeros';
import { cIC, rIC } from '@/utils/requestIdleCallbackShim';

type AnimatedCountProps = ComponentProps<typeof Box> & {
  number: number;
  duration?: number;
  component: ElementType;
};

const DEFAULT_DURATION = 360;

export const AnimatedCount = ({
  number,
  duration = DEFAULT_DURATION,
  children,
  ...props
}: AnimatedCountProps) => {
  const previousValueRef = useRef(number);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const targetLengthRef = useRef(String(number).length);
  const displayRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (previousValueRef.current === number) {
      if (displayRef.current) {
        displayRef.current.textContent = `${number}`;
      }
      return;
    }

    const startValue = Number.isNaN(previousValueRef.current) ? 0 : previousValueRef.current;
    const endValue = number;
    const difference = endValue - startValue;
    targetLengthRef.current = String(number).length;

    const animate = (currentTime: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuad = 1 - (1 - progress) ** 2;
      const currentValue = startValue + difference * easeOutQuad;

      if (displayRef.current) {
        displayRef.current.textContent = padWithZeros(
          Math.round(currentValue),
          targetLengthRef.current,
        );
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        previousValueRef.current = endValue;
        startTimeRef.current = null;
      }
    };

    const start = () => {
      rafRef.current = requestAnimationFrame(animate);
    };

    const rICId = rIC(start);

    return () => {
      cIC(rICId);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [number, duration]);

  return <Box {...props} ref={displayRef} />;
};
